'use client';
import { Icons } from '@/components/icons';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { useSystemHealth } from '@/hooks/use-system-health';
import { cn } from '@/lib/cn';
import { GlobalFilterSelector } from '@/lib/global-filter/components/global-filter-selector';
import { GlobalFilterTrigger } from '@/lib/global-filter/components/global-filter-trigger';
import { GlobalSearchTrigger } from '@/lib/global-search-v2/switch';
import { Urls } from '@/lib/urls';
import { MenuProvider } from '@/providers/menu-provider';
import { ActivityIcon } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Logo } from '../ui/logo';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { DashboardNavigation } from './dashboard/dashboard-navigation';
import SettingsNavigation from './settings/settings-navigation';
import { FloatingMenu } from './shared/floating-menu';
import SystemStatus from './shared/floating-menu/components/system-status';
import { OrganizationSelector } from './shared/organization-selector';

export function Sidebar({
  variant,
}: {
  variant: 'dashboard' | 'settings' | 'documents';
}) {
  const [systemStatusOpen, setSystemStatusOpen] = useState(false);

  const { data: systemHealth } = useSystemHealth();

  const renderMainSidebar = variant === 'dashboard' || variant === 'documents';

  // With global-search-v2 the filter button routes into the ⌘K palette's
  // "Filtrera i systemet" page instead of the legacy modal.
  const isGlobalSearchV2 = useFeatureFlag(FEATURE_FLAGS.GLOBAL_SEARCH_V2);

  const renderSettingsSidebar = variant === 'settings';

  return (
    <>
      {systemStatusOpen && (
        <SystemStatus closeFunction={() => setSystemStatusOpen(false)} />
      )}

      <MenuProvider>
        <nav className='relative flex h-screen max-h-window-keeper-height w-full max-w-75 min-w-67.5 flex-col border-r bg-surface p-0 shadow-[inset_0px_0px_10px_rgba(0,0,0,0.05)]'>
          {variant === 'settings' && (
            <div className='border-b p-4 py-4.75'>
              <div className='flex items-center justify-between'>
                <Link
                  prefetch={'auto'}
                  href='/dashboard'
                  className='flex items-center bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden'
                >
                  <Icons.previous className='mr-2 size-5' />
                  <h2>Kontrollcenter</h2>
                </Link>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => setSystemStatusOpen(true)}
                      variant={'outline'}
                      size={'icon'}
                      className={cn('relative', {
                        'animate-pulse bg-yellow-200':
                          !!systemHealth?.maintenance?.eta,
                        'animate-pulse bg-destructive-subtle':
                          systemHealth?.maintenance?.active,
                      })}
                    >
                      <ActivityIcon className='size-4 text-foreground' />
                      <div
                        className={cn(
                          'absolute -top-1 -right-1 size-3 rounded-full border border-background',
                          {
                            'bg-success':
                              !systemHealth?.maintenance?.active &&
                              !systemHealth?.maintenance?.eta,
                            'bg-yellow-600': !!systemHealth?.maintenance?.eta,
                            'bg-destructive': systemHealth?.maintenance?.active,
                          }
                        )}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Systemstatus</TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
          {(variant === 'dashboard' || variant === 'documents') && (
            <div className='flex flex-col items-center gap-3 p-4 pb-2'>
              <div className='flex w-full items-start gap-4'>
                <Link
                  prefetch={'auto'}
                  href={Urls.dashboard.home}
                  className='shrink-0'
                >
                  <Logo />
                </Link>
                <OrganizationSelector />
              </div>
              {isGlobalSearchV2 ? (
                <div className='flex w-full items-center gap-2'>
                  <GlobalSearchTrigger showBetaBadge={true} />
                  <GlobalFilterTrigger />
                </div>
              ) : (
                <>
                  <GlobalFilterSelector />
                  <GlobalSearchTrigger showBetaBadge={false} />
                </>
              )}
            </div>
          )}
          <div className='relative min-w-full'>
            <div className='absolute inset-x-0 z-10 min-h-3 bg-linear-to-b from-surface to-transparent dark:from-surface'></div>
          </div>
          <div className='scrollbar-hide overflow-y-scroll pb-28'>
            {renderMainSidebar && <DashboardNavigation />}
            {renderSettingsSidebar && <SettingsNavigation />}
          </div>
          <FloatingMenu isSettings={variant === 'settings'} />
        </nav>
      </MenuProvider>
    </>
  );
}
